import {
  Editor,
  Point,
  Range,
  Element as SlateElement,
  type Element as SlateElementType,
  Transforms,
} from 'slate';

import type {
  BulletedListElement,
  CustomEditor,
  CustomElement,
  NumberedListElement,
} from '../types/SlateNodes';

/**
 * Inline Markdown shortcuts the composer recognises while typing.
 * Triggered when the user types one of these followed by a space at
 * the start of a block. Headings are intentionally absent — the chat
 * design doesn't expose them and PopshopRN keeps them commented out.
 */
const SHORTCUTS: Record<string, CustomElement['type']> = {
  '*': 'list-item',
  '-': 'list-item',
  '+': 'list-item',
  '>': 'block-quote',
};

/**
 * Slate plugin that turns `* `, `- `, `+ `, `1. `, `> ` typed at the
 * start of a block into the corresponding rich-text element. Direct
 * port of PopshopRN's `withMarkdownShortcuts` minus the
 * Android-pending-diffs handler (the web target doesn't need the
 * Slate Android IME flush).
 *
 * Element narrowing uses local casts rather than Slate's
 * `declare module 'slate'` augmentation — another workspace slice
 * already augments globally.
 */
export function withMarkdownShortcuts(editor: CustomEditor): CustomEditor {
  const { deleteBackward, insertText } = editor;

  editor.insertText = text => {
    const { selection } = editor;

    if (text.endsWith(' ') && selection && Range.isCollapsed(selection)) {
      const { anchor } = selection;
      const block = Editor.above(editor, {
        match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
      });
      const path = block ? block[1] : [];
      const start = Editor.start(editor, path);
      const range = { anchor, focus: start };
      const beforeText = Editor.string(editor, range) + text.slice(0, -1);

      let type: CustomElement['type'] | undefined = SHORTCUTS[beforeText];
      let isOrderedListItem = false;
      if (!type && beforeText.match(/^1\.$/) && text === ' ') {
        type = 'list-item';
        isOrderedListItem = true;
      }

      if (type) {
        Transforms.select(editor, range);
        if (!Range.isCollapsed(range)) {
          Transforms.delete(editor);
        }
        Transforms.setNodes(
          editor,
          { type } as unknown as Partial<SlateElementType>,
          {
            match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
          },
        );
        if (type === 'list-item') {
          const list: BulletedListElement | NumberedListElement = {
            type: isOrderedListItem ? 'numbered-list' : 'bulleted-list',
            children: [],
          };
          Transforms.wrapNodes(editor, list as unknown as SlateElementType, {
            match: n =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              (n as CustomElement).type === 'list-item',
          });
        }
        return;
      }
    }

    insertText(text);
  };

  editor.deleteBackward = (...args) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const match = Editor.above(editor, {
        match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
      });
      if (!match) {
        deleteBackward(...args);
        return;
      }
      const [block, path] = match;
      const blockType = (block as unknown as CustomElement).type;
      const start = Editor.start(editor, path);
      if (
        !Editor.isEditor(block) &&
        SlateElement.isElement(block) &&
        blockType !== 'paragraph' &&
        Point.equals(selection.anchor, start)
      ) {
        Transforms.setNodes(editor, {
          type: 'paragraph',
        } as unknown as Partial<SlateElementType>);
        if (blockType === 'list-item') {
          Transforms.unwrapNodes(editor, {
            match: n => {
              if (Editor.isEditor(n) || !SlateElement.isElement(n))
                return false;
              const t = (n as CustomElement).type;
              return t === 'bulleted-list' || t === 'numbered-list';
            },
            split: true,
          });
        }
        return;
      }
    }

    deleteBackward(...args);
  };

  return editor;
}
